const PUBLIC_PREFIXES = new Set(['auth', 'microsoft-auth-callback', 'unsubscribe', 'account', 'tenant-select']);

// App route prefixes that are not tenant codes.
const RESERVED_APP_PREFIXES = new Set([
  'no-tenant',
  'home',
  'chat',
  'write',
  'review',
  'reporting',
  'tutorials',
  'groups',
  'actions',
  'meetings',
  'wiki',
  'docs',
  'modules',
  'dashboard',
  'profile',
  'settings',
  'admin',
  'data-library',
  'landing',
  'notifications',
  'files',
  'helpers',
  'search',
  'surveys',
  'permissions',
  'specialists',
  'customerio',
  'asset-reviews'
]);

export function isReservedAppPrefix(value: string | null | undefined): boolean {
  if (!value) return false;
  return RESERVED_APP_PREFIXES.has(value);
}

export function getTenantFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const [first] = parts;
  if (PUBLIC_PREFIXES.has(first) || isReservedAppPrefix(first)) {
    return null;
  }
  return first;
}

/**
 * Team group id from a group-hub path (`/.../groups/{id}/...`).
 * The groups directory has no id. Dashboard card routes use `/group/` (singular)
 * and are not team groups.
 */
export function getGroupIdFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  const groupsIndex = parts.indexOf('groups');
  if (groupsIndex === -1) {
    return null;
  }
  return parts[groupsIndex + 1] || null;
}

/** Remember the organisation from a tenant URL so unscoped routes like `/admin` keep the same nav. */
export function rememberActiveTenantCode(code: string): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?.active_tenant_code === code) return;
    parsed.active_tenant_code = code;
    localStorage.setItem('user', JSON.stringify(parsed));
  } catch {
    /* ignore */
  }
}

export function getStoredTenantCode(): string | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.active_tenant_code) {
      return parsed.active_tenant_code;
    }
    if (parsed?.tenant_code) {
      return parsed.tenant_code;
    }
    if (Array.isArray(parsed?.tenants) && parsed.tenants.length === 1) {
      return parsed.tenants[0]?.code ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

function tenantCodeIfMember(code: string | null, tenants: { code: string }[] | null | undefined): string | null {
  if (!code) {
    return null;
  }
  if (tenants && tenants.length > 0 && !tenants.some((t) => t.code === code)) {
    return null;
  }
  return code;
}

/**
 * Tenant from URL path first (`getTenantFromPath`), else from cached profile (`getStoredTenantCode`).
 * When `tenants` is non-empty, the code must match a membership (skipped if profile not loaded yet).
 */
export function resolveTenantCode(pathname?: string, tenants?: { code: string }[] | null): string | null {
  if (pathname) {
    const fromPath = getTenantFromPath(pathname);
    const fromPathOk = tenantCodeIfMember(fromPath, tenants);
    if (fromPathOk) {
      return fromPathOk;
    }
  }
  return tenantCodeIfMember(getStoredTenantCode(), tenants);
}

export function isOrganisationTenantAtPath(
  pathname: string | undefined,
  tenants: { code: string; tenant_type?: string | null }[] | null | undefined
): boolean {
  const code = resolveTenantCode(pathname, tenants);
  if (!code) {
    return false;
  }
  const row = tenants?.find((t) => t.code === code);
  return row?.tenant_type === 'organisation';
}

export function prefixWithTenant(path: string, tenantCode: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/${tenantCode}${normalizedPath}`;
}

/** Pathname plus its query string and hash, so redirects keep URL parameters. */
export function withLocationParams(pathname: string, location: { search?: string; hash?: string }): string {
  return `${pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

/** Login URL that returns the user to the same path, query string, and hash. */
export function loginRedirectTo(location: { pathname: string; search?: string; hash?: string }): string {
  return `/auth/login?redirect=${encodeURIComponent(withLocationParams(location.pathname, location))}`;
}

export function isPublicPath(path: string): boolean {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return (
    normalizedPath.startsWith('/auth') ||
    normalizedPath.startsWith('/microsoft-auth-callback') ||
    normalizedPath.startsWith('/unsubscribe') ||
    normalizedPath.startsWith('/account') ||
    normalizedPath.startsWith('/profile') ||
    normalizedPath.startsWith('/tutorials') ||
    normalizedPath.startsWith('/admin')
  );
}

function splitHash(path: string): { beforeHash: string; hash: string } {
  const hashIndex = path.indexOf('#');
  if (hashIndex === -1) {
    return { beforeHash: path, hash: '' };
  }
  return { beforeHash: path.slice(0, hashIndex), hash: path.slice(hashIndex) };
}

export function toTenantPath(path: string, tenantCode: string | null): string {
  if (!path || !tenantCode) {
    return path;
  }
  if (/^(https?:)?\/\//.test(path) || path.startsWith('mailto:') || path.startsWith('tel:')) {
    return path;
  }
  if (!path.startsWith('/')) {
    return path;
  }

  // Split the hash off before pathname checks. Highlight links and other
  // parameters live in the hash (`#element_ids=...`) and must survive prefixing.
  const { beforeHash, hash } = splitHash(path);
  if (isPublicPath(beforeHash)) {
    return path;
  }

  const [pathname, query = ''] = beforeHash.split('?', 2);
  const hadTrailingSlash = pathname.length > 1 && pathname.endsWith('/');
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const firstSegment = normalizedPathname.split('/').filter(Boolean)[0];
  if (firstSegment === tenantCode) {
    return path;
  }
  const tenantPath = `/${tenantCode}${normalizedPathname}`;
  const withSlash = hadTrailingSlash ? `${tenantPath}/` : tenantPath;
  return `${withSlash}${query ? `?${query}` : ''}${hash}`;
}

export function normalizeTenantRedirectPath(path: string | null, tenantCode: string | null): string {
  if (!path) {
    return tenantCode ? prefixWithTenant('/home', tenantCode) : '/home';
  }
  if (!tenantCode) {
    return path;
  }
  const trimmed = path.replace(/\/+$/, '');
  if (trimmed === '' || trimmed === '/' || trimmed === `/${tenantCode}`) {
    return prefixWithTenant('/home', tenantCode);
  }
  return toTenantPath(path, tenantCode);
}
